import React, { useState, useEffect } from 'react';
import { Button } from '@/components/ui/button';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Clock, Coffee } from 'lucide-react';

interface BreakEntry {
  id: string;
  startTime: Date;
  endTime?: Date;
  duration?: number;
}

interface BreakTrackerProps {
  onBreakUpdate: (breaks: BreakEntry[]) => void;
}

const BreakTracker: React.FC<BreakTrackerProps> = ({ onBreakUpdate }) => {
  const [breaks, setBreaks] = useState<BreakEntry[]>([]);
  const [currentBreak, setCurrentBreak] = useState<BreakEntry | null>(null);
  const [breakTime, setBreakTime] = useState(0);

  useEffect(() => {
    let interval: NodeJS.Timeout;
    if (currentBreak) {
      interval = setInterval(() => {
        const now = new Date();
        const elapsed = Math.floor((now.getTime() - currentBreak.startTime.getTime()) / 1000);
        setBreakTime(elapsed);
      }, 1000);
    }
    return () => clearInterval(interval);
  }, [currentBreak]);

  const startBreak = () => {
    const newBreak: BreakEntry = {
      id: Date.now().toString(),
      startTime: new Date()
    };
    setCurrentBreak(newBreak);
    setBreakTime(0);
  };

  const endBreak = () => {
    if (currentBreak) {
      const endTime = new Date();
      const duration = Math.floor((endTime.getTime() - currentBreak.startTime.getTime()) / 1000 / 60);
      const completedBreak = {
        ...currentBreak,
        endTime,
        duration
      };
      
      const updatedBreaks = [...breaks, completedBreak];
      setBreaks(updatedBreaks);
      setCurrentBreak(null);
      setBreakTime(0);
      onBreakUpdate(updatedBreaks);
    }
  };

  const formatTime = (seconds: number) => {
    const mins = Math.floor(seconds / 60);
    const secs = seconds % 60;
    return `${mins.toString().padStart(2, '0')}:${secs.toString().padStart(2, '0')}`;
  };

  const totalBreakTime = breaks.reduce((total, b) => total + (b.duration || 0), 0);

  return (
    <Card>
      <CardHeader>
        <CardTitle className="flex items-center gap-2">
          <Coffee className="h-5 w-5" />
          Break Tracker
        </CardTitle>
      </CardHeader>
      <CardContent className="space-y-4">
        <div className="flex items-center justify-between">
          {currentBreak ? (
            <div className="flex items-center gap-4">
              <div className="text-lg font-mono">
                <Clock className="inline h-4 w-4 mr-1" />
                {formatTime(breakTime)}
              </div>
              <Button onClick={endBreak} variant="destructive">
                Finish Break
              </Button>
            </div>
          ) : (
            <Button onClick={startBreak} className="bg-orange-500 hover:bg-orange-600">
              Start Break
            </Button>
          )}
        </div>
        
        {breaks.length > 0 && (
          <div className="text-sm text-gray-600">
            <p>Total break time today: {totalBreakTime} minutes</p>
            <p>Breaks taken: {breaks.length}</p>
          </div>
        )}
      </CardContent>
    </Card>
  );
};

export default BreakTracker;
export type { BreakEntry };